import type { PublicPage } from '../types';
import { t, type Locale } from '@shared/i18n';
import { useI18n } from '../lib/i18n';
import { PaymentMethodIcon } from './Icons';

/** The booking-page fields that describe how a customer pays. */
export type PaymentFields = Partial<Pick<PublicPage, 'accepts_cash' | 'accepts_card_in_person' | 'zelle_recipient' | 'payment_instructions'>>;

type PaymentMethodKind = 'cash' | 'card' | 'zelle';

interface PaymentMethodItem {
  kind: PaymentMethodKind;
  label: string;
}

function paymentMethodItems(page: PaymentFields, locale: Locale, revealZelleRecipient: boolean): PaymentMethodItem[] {
  const out: PaymentMethodItem[] = [];
  if (page.accepts_cash) out.push({ kind: 'cash', label: t(locale, 'pay.cash') });
  if (page.accepts_card_in_person) out.push({ kind: 'card', label: t(locale, 'pay.card_in_person') });
  const zelle = page.zelle_recipient?.trim();
  if (zelle) {
    out.push({
      kind: 'zelle',
      label: revealZelleRecipient ? t(locale, 'pay.zelle_to', { recipient: zelle }) : 'Zelle®',
    });
  }
  return out;
}

/**
 * Accepted methods in plain words, in the order a customer would expect
 * them. The helper keeps its historical default of including the Zelle
 * recipient for server/tests that call it directly. Public UI components
 * choose explicitly whether that recipient belongs on the screen.
 */
export function paymentMethods(page: PaymentFields, locale: Locale = 'en', revealZelleRecipient = true): string[] {
  return paymentMethodItems(page, locale, revealZelleRecipient).map((method) => method.label);
}

export function hasPaymentInfo(page: PaymentFields): boolean {
  return paymentMethodItems(page, 'en', false).length > 0 || Boolean(page.payment_instructions?.trim());
}

interface BodyProps {
  page: PaymentFields;
  /** Formatted price of the service being booked, when the business shows prices. */
  price?: string | null;
  /** Formatted deposit, when the business asks for one. */
  deposit?: string | null;
  /** True once the business has recorded the deposit as received. */
  depositPaid?: boolean;
  /**
   * A Zelle phone number or email is payment routing information. Keep it
   * off discovery, review and public marketing surfaces; reveal it only
   * after a booking when the customer actually needs to pay.
   */
  revealZelleRecipient?: boolean;
}

/** Methods, instructions and (optionally) the price — without a heading, for use inside a policy panel. */
export function PaymentMethods({ page, price, deposit, depositPaid, revealZelleRecipient = false }: BodyProps) {
  const { t: translate, locale } = useI18n();
  const methods = paymentMethodItems(page, locale, revealZelleRecipient);
  const instructions = page.payment_instructions?.trim();
  if (methods.length === 0 && !instructions && !price && !deposit) return null;
  return (
    <div className="pay">
      {price && <p className="pay-price">{price}</p>}
      {deposit && (
        <p className="pay-deposit">
          {depositPaid
            ? translate('pay.deposit_received', { amount: deposit })
            : translate('pay.deposit_required', { amount: deposit })}
        </p>
      )}
      {methods.length > 0 && (
        <ul className="pay-methods" aria-label={translate('pay.accepted_methods')}>
          {methods.map((method) => (
            <li key={method.kind}>
              <PaymentMethodIcon method={method.kind} />
              <span>{method.label}</span>
            </li>
          ))}
        </ul>
      )}
      {instructions && <p className="pay-instructions">{instructions}</p>}
    </div>
  );
}

/** "How to pay" with its heading, for the confirmation and self-service pages. */
export function HowToPay({ page, price, deposit, depositPaid }: BodyProps) {
  const { t: translate } = useI18n();
  if (!hasPaymentInfo(page) && !price && !deposit) return null;
  return (
    <section className="pay-block" aria-labelledby="pay-h">
      <h3 id="pay-h" className="pay-title">{translate('policies.how_to_pay')}</h3>
      <PaymentMethods
        page={page}
        price={price}
        deposit={deposit}
        depositPaid={depositPaid}
        revealZelleRecipient
      />
    </section>
  );
}

/** Shown only when the business has recorded the appointment as paid. Never says "unpaid". */
export function PaidBadge({ status }: { status?: string | null }) {
  const { t: translate } = useI18n();
  const keys = {
    paid: 'pay.status_paid', waived: 'pay.status_waived', card_on_file: 'pay.status_card_on_file',
    deposit_paid: 'pay.status_deposit_paid', partially_paid: 'pay.status_partially_paid',
    refunded: 'pay.status_refunded', partially_refunded: 'pay.status_partially_refunded',
    payment_failed: 'pay.status_payment_failed', cancellation_fee_eligible: 'pay.status_fee_review',
    cancellation_fee_paid: 'pay.status_fee_paid',
  } as const;
  const key = keys[(status ?? '') as keyof typeof keys];
  if (!key) return null;
  const label = translate(key);
  return <span className={status==='paid'?'badge badge--paid':'badge'}>{label}</span>;
}
